import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Button } from '@app/components';
import {
  createBatchUpload,
  validateAutoMarkFile,
} from '@app/services/studentAutoMarking';
import { getStudentById } from '@app/services/student';
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import { encodeId } from '@app/helpers/base64url';

import './StudentAutoMarking.css';

// ─── Upload Dropzone Sub-component (multi-file) ──────────────────────────────

interface DropzoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  maxFiles?: number;
}

const Dropzone = ({ onFiles, accept, maxFiles = 20 }: DropzoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const filesArray = Array.from(fileList).slice(0, maxFiles);
    if (filesArray.length > 0) onFiles(filesArray);
  };

  const onDragOver  = (e: React.DragEvent) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop      = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div
      className={`sam-upload-dropzone${dragging ? ' dragover' : ''}`}
      onClick={() => inputRef.current?.click()}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept || 'image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg'}
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
        multiple
      />
      <div className="upload-icon"><i className="fas fa-cloud-upload-alt" /></div>
      <p className="mb-1 font-weight-600">Drag &amp; drop or click to upload</p>
      <small className="text-muted">Supports: JPG, JPEG, PNG (up to {maxFiles} files, max 5 MB each)</small>
    </div>
  );
};

// ─── Simple Upload Indicator ──────────────────────────────────────────────────

const UploadingOverlay = () => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(30, 30, 30, 0.85)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    color: '#fff',
  }}>
    <div className="spinner-border text-light" role="status" style={{ width: '4rem', height: '4rem', marginBottom: '1.5rem' }}>
      <span className="sr-only">Uploading...</span>
    </div>
    <h3 style={{ fontWeight: 600, fontSize: '1.4rem', marginBottom: 12, letterSpacing: 0.3 }}>
      Uploading worksheets...
    </h3>
    <p style={{ opacity: 0.8, fontSize: '0.95rem' }}>
      Please wait while we process your batch upload
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const UploadStudentAutoMarking = () => {
  const navigate  = useNavigate();
  const studentIdFromUrl = useDecodedParam('studentId', '/students');
  const hasStudentParam  = !!window.location.pathname.match(/\/student\/[^/]+\/auto-marking/);
  const presetStudentId  = hasStudentParam ? studentIdFromUrl : null;

  const isMounted = useRef(true);
  const canAccess = useSelector((state: any) => state.auth.permissions?.['student-auto-marking']);

  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (presetStudentId) {
      preSelectStudent(presetStudentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const preSelectStudent = async (studentId: number) => {
    try {
      const resp = await getStudentById(studentId);
      if (resp?.success && isMounted.current) {
        setSelectedStudent(resp.collections);
      }
    } catch { /* silent */ }
  };

  const handleFiles = (newFiles: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of newFiles) {
      const validation = validateAutoMarkFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    }

    if (errors.length > 0) {
      toast.error(`Some files were invalid: ${errors[0]}`);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => {
        const combined = [...prev, ...validFiles];
        return combined.slice(0, 20); // Limit to 20 files max
      });
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setFiles([]);
  };

  const isFormValid = selectedStudent && files.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.warning('Please upload at least one file.');
      return;
    }

    if (files.length > 20) {
      toast.error('Maximum 20 files allowed per batch.');
      return;
    }

    const formData = new FormData();
    formData.append('student_id', String(selectedStudent.id));
    // Append all files with field name 'images' (plural) as required by multer.array('images', 20)
    files.forEach((file) => {
      formData.append('images', file);
    });

    try {
      setSubmitting(true);
      const resp = await createBatchUpload(formData);
      if (!isMounted.current) return;

      if (resp?.success && resp?.data?.batch_id) {
        toast.success(`Batch created successfully! Processing ${resp.data.total} worksheet(s).`);
        const batchId = resp.data.batch_id;
        const studentEncodedId = encodeId(selectedStudent.id);
        // Navigate to batch progress page
        navigate(`/student/${studentEncodedId}/auto-marking/batch/${encodeId(batchId)}`);
      } else {
        const errorMsg = resp?.errors?.[0]?.msg || resp?.message || 'Batch upload failed. Please try again.';
        toast.error(errorMsg);
        
        // If batch_id is returned in error response (failed batch retained), show it
        if (resp?.data?.batch_id) {
          toast.info(`Batch ID ${resp.data.batch_id} was created but failed. Check the progress page for details.`);
          const batchId = resp.data.batch_id;
          const studentEncodedId = encodeId(selectedStudent.id);
          navigate(`/student/${studentEncodedId}/auto-marking/batch/${encodeId(batchId)}`);
        }
      }
    } catch (err: any) {
      if (isMounted.current) {
        toast.error(err?.message || 'Internal Server Error');
      }
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (canAccess && !canAccess.create) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="alert alert-warning mt-3">
              <i className="fas fa-exclamation-triangle mr-2" />
              You do not have permission to upload worksheets.
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      {isSubmitting && <UploadingOverlay />}

      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                Batch Upload &amp; Auto-Mark
                {selectedStudent && (
                  <small className="ml-2 text-muted" style={{ fontSize: '1rem', fontWeight: 400 }}>
                    — {[selectedStudent.first_name, selectedStudent.middle_name, selectedStudent.last_name].filter(Boolean).join(' ')}
                  </small>
                )}
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/students">Students</Link></li>
                {selectedStudent && presetStudentId && (
                  <li className="breadcrumb-item">
                    <Link to={`/student/${encodeId(presetStudentId)}/auto-marking`}>
                      {[selectedStudent.first_name, selectedStudent.middle_name, selectedStudent.last_name].filter(Boolean).join(' ')}
                    </Link>
                  </li>
                )}
                {selectedStudent && !presetStudentId && selectedStudent.id && (
                  <li className="breadcrumb-item">
                    <Link to={`/student/${encodeId(selectedStudent.id)}/auto-marking`}>
                      {[selectedStudent.first_name, selectedStudent.middle_name, selectedStudent.last_name].filter(Boolean).join(' ')}
                    </Link>
                  </li>
                )}
                <li className="breadcrumb-item active">Batch Upload</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">

          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-lg-8 offset-lg-2">
                {/* ── Info banner (same column as the form card so widths line up) ── */}
                <div className="alert alert-info d-flex align-items-start gap-2 mb-3" style={{ borderLeft: '4px solid #3b82f6', borderRadius: 6, fontSize: '0.95rem', padding: '8px 12px' }}>
                  <i className="fas fa-info-circle mr-2" style={{ color: '#2563eb', fontSize: '0.95rem', flexShrink: 0, marginTop: 4 }} />
                  <div>
                    <div><strong>Batch Upload</strong> — Subject, module, and worksheet details will be automatically detected from each image.</div>
                    <div>Upload up to 20 worksheet images at once.</div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">
                      <i className="fas fa-cloud-upload-alt mr-2" />
                      Upload Worksheets
                    </h3>
                  </div>
                  <div className="card-body">

                    {/* File Upload (student comes from the URL; shown in the page header) */}
                    <div className="form-group">
                      <label className="font-weight-600">
                        Worksheet Images <span className="text-danger">*</span>
                        <small className="text-muted ml-2">(1-20 files)</small>
                      </label>
                      
                      {files.length === 0 ? (
                        <Dropzone onFiles={handleFiles} maxFiles={20} />
                      ) : (
                        <>
                          {/* File list */}
                          <div className="mb-2" style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px' }}>
                            {files.map((file, index) => (
                              <div
                                key={index}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 10,
                                  border: '1px solid #e5e7eb', borderRadius: 6,
                                  padding: '8px 12px', background: '#f9fafb',
                                  marginBottom: files.length - 1 === index ? 0 : 6,
                                }}
                              >
                                <i className="fas fa-file-image text-primary" />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {file.name}
                                  </div>
                                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(index)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Remove file"
                                >
                                  <i className="fas fa-times" />
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Add more / Clear all */}
                          <div className="d-flex mt-2" style={{ gap: '8px' }}>
                            {files.length < 20 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const input = document.createElement('input');
                                  input.type = 'file';
                                  input.accept = 'image/png,image/jpeg,image/jpg,.png,.jpg,.jpeg';
                                  input.multiple = true;
                                  input.onchange = (e: any) => {
                                    const newFiles = e.target.files;
                                    if (newFiles) handleFiles(Array.from(newFiles));
                                  };
                                  input.click();
                                }}
                                className="btn btn-sm btn-outline-primary"
                              >
                                <i className="fas fa-plus mr-1" /> Add More ({files.length}/20)
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={handleClearAll}
                              className="btn btn-sm btn-outline-secondary"
                            >
                              <i className="fas fa-trash mr-1" /> Clear All
                            </button>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="d-flex justify-content-between mt-4">
                      <Link
                        to={presetStudentId
                          ? `/student/${encodeId(presetStudentId)}/auto-marking`
                          : selectedStudent?.id
                          ? `/student/${encodeId(selectedStudent.id)}/auto-marking`
                          : '/students'}
                        className="btn btn-outline-secondary"
                      >
                        <i className="fas fa-times mr-1" />
                        Cancel
                      </Link>
                      <Button
                        type="submit"
                        isLoading={isSubmitting}
                        className="btn btn-primary"
                        disabled={!isFormValid || isSubmitting}
                      >
                        <i className="fas fa-upload mr-1" />
                        Upload & Process ({files.length} {files.length === 1 ? 'file' : 'files'})
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>
    </>
  );
};

export default UploadStudentAutoMarking;
